import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { FormControlLabel, Radio, RadioGroup } from "@mui/material";

// Styles
import NewNotesContainer from "./NewNotes.styles";

// Contexts
import { useBQMQ } from "context/BQMQ.context";
import { useSettings } from "context/Settings.context";

// Components
import Button from "components/Common/Button/Button";
import DateSelect from "components/Common/DateSelect/DateSelect";
import LoadingSpinner from "components/Common/LoadingSpinner/LoadingSpinner";
import Select from "components/Common/Select/Select";
import RichText from "components/Common/RichText/RichText";
import MultipleFileUpload from "components/Common/MultipleFileUpload/MultipleFileUpload";
import TextField from "../../../components/Common/TextField/TextField";

// Utils
import * as Database from "utils/database.utils";
import { swapDayAndMonth } from "utils/date.utils";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import {
    DropDownType,
    FileUploadResponse,
    LocationState,
    MorningMeetingsDataType,
    MorningMeetingNoteToEdit,
    SelectOption,
    Topic,
} from "./NewNotes.types";
import { getAnalysts } from "../../../utils/bqmq.utils";
import {
    AgendaItem,
    MorningMeetingDetailsDto,
} from "../MorningMeetingDetails/MorningMeetingDetails.types";
import { MorningMeeting } from "../MorningMeetings/MorningMeetings.types";
interface NewNotesProps {}

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

type NoteType = "standalone" | "linked";

const NewNotes = ({}: NewNotesProps) => {
    const history = useHistory();
    const location = useLocation<LocationState>();

    const [noteType, setNoteType] = useState<NoteType>("standalone");

    const [chosenCompany, setChosenCompany] = useState<string | null>(null);
    const [chosenAnalyst, setChosenAnalyst] = useState<string>("");
    const [chosenDate, setChosenDate] = useState<any>(new Date());
    const [chosenTopic, setChosenTopic] = useState<string>("");
    const [chosenAgendaItemId, setChosenAgendaItemId] = useState<string>("");
    const [noteText, setNoteText] = useState<string>("");

    const [showCompanySelect, setShowCompanySelect] = useState(false);
    const [showPotentialCompanySelect, setShowPotentialCompanySelect] =
        useState(false);
    const [useCustomCompany, setUseCustomCompany] = useState(false);
    const [customCompanyName, setCustomCompanyName] = useState<string>("");
    const [showFileSelect, setShowFileSelect] = useState(false);

    const [nonFocusListCompanies, setNonFocusListCompanies] = useState<
        SelectOption[]
    >([]);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [users, setUsers] = useState<DropDownType[]>([]);
    const [updatedCompanies, setUpdatedCompanies] = useState<SelectOption[]>(
        []
    );
    const [originalData, setOriginalData] = useState<MorningMeetingsDataType[]>(
        []
    );
    const [meetingId, setMeetingId] = useState<string>("");
    const [topicsOptions, setTopicsOptions] = useState<SelectOption[]>([]);
    const [analysts, setAnalysts] = useState<any[]>([]);
    const [originalConfirmed, setOriginalConfirmed] = useState(true);

    const [morningMeeting, setMorningMeetings] = useState<MorningMeeting[]>([]);
    const [chosenMeetingId, setChosenMeetingId] = useState<string>("");
    const [meetingDetails, setMeetingDetails] =
        useState<MorningMeetingDetailsDto | null>(null);

    const [loading, setLoading] = useState<boolean>(false);

    const { companies } = useBQMQ();
    const { setShowLoadingToast, setToast } = useSettings();

    const query = useQuery();
    const meetingNoteIdFromEdit = query.get("meeting_note_id");
    const isEditing = !!meetingNoteIdFromEdit;

    useEffect(() => {
        if (meetingNoteIdFromEdit) {
            setMeetingId(meetingNoteIdFromEdit);
            const fetchMeetingNoteById = async () => {
                const response = await Database.get(
                    `api/bqmq_voting/meeting-note/${meetingNoteIdFromEdit}`
                );

                if (response.ok) {
                    const data: { data: MorningMeetingNoteToEdit } =
                        await response.json();

                    setChosenCompany(data.data.company_id);
                    setChosenAnalyst(data.data.analyst_id);
                    setChosenDate(swapDayAndMonth(data.data.date.toString()));
                    setNoteText(data.data.notes.join("<br/><br/>"));
                    setChosenTopic(data.data.topic_classifier_id);
                    setOriginalConfirmed(data.data.confirmed);
                    setShowFileSelect(true);

                    if (data.data.agenda_item_id) {
                        setNoteType("linked");
                        setChosenAgendaItemId(data.data.agenda_item_id);
                        if (data.data.morning_meeting_id) {
                            setChosenMeetingId(data.data.morning_meeting_id);
                        }
                    } else {
                        setNoteType("standalone");
                    }
                }
            };
            fetchMeetingNoteById();
        }
    }, [meetingNoteIdFromEdit]);

    const fetchCompaniesAnalysts = async (): Promise<
        MorningMeetingsDataType[] | undefined
    > => {
        const response: Response = await Database.get(
            "api/bqmq_voting/get-new-note-dropdown-items"
        );

        if (response.ok) {
            const data = await response.json();
            return data.data as MorningMeetingsDataType[];
        }
    };

    const fetchTopics = async (): Promise<Topic[] | undefined> => {
        const response: Response = await Database.get(
            "api/bqmq_voting/morning-meetings/topics"
        );

        if (response.ok) {
            const data = await response.json();
            return data.data as Topic[];
        }
    };

    const fetchAllMorningMeetings = async (): Promise<
        MorningMeeting[] | undefined
    > => {
        const response = await Database.get(
            "api/bqmq_voting/morning-meetings?include_cancelled=true"
        );

        if (response.ok) {
            const data = await response.json();
            return data.data as MorningMeeting[];
        }
    };

    const fetchMorningMeetingDetails = async (
        morningMeetingId: string
    ): Promise<MorningMeetingDetailsDto | undefined> => {
        const response = await Database.get(
            `api/bqmq_voting/morning-meetings/${morningMeetingId}`
        );
        if (response.ok) {
            const data = await response.json();
            return data.data as MorningMeetingDetailsDto;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);

            const [companiesAnalystsData, topicsData, _, meetingsData] =
                await Promise.all([
                    fetchCompaniesAnalysts(),
                    fetchTopics(),
                    // Need to fetch all analysts, because custom speakers (non-entra users)
                    // might have some notes assigned to them
                    getAnalysts(setAnalysts, "all"),
                    fetchAllMorningMeetings(),
                ]);

            if (companiesAnalystsData) {
                setOriginalData(companiesAnalystsData);
            }

            if (topicsData) {
                setTopicsOptions(
                    topicsData.map((topic: Topic) => ({
                        value: topic.id,
                        label: topic.value,
                    }))
                );
            }

            if (meetingsData) {
                setMorningMeetings(meetingsData);
            }

            setLoading(false);
        };

        fetchData();
    }, []);

    useEffect(() => {
        setUsers(analysts.filter((a: DropDownType) => a.label && a.active));
        setUpdatedCompanies(companies.filter((c: SelectOption) => c.label));
        setNonFocusListCompanies(
            companies.filter(
                (c: SelectOption) => c.label && (c as any).focus_list_flag !== 1
            )
        );
    }, [analysts, companies]);

    // Effect for automatically setting the analyst when a company is selected. Does not run when editing a linked note.
    useEffect(() => {
        if (!chosenCompany || noteType === "linked") return;

        const foundCompany = originalData.find(
            (d) => d.company_id === chosenCompany
        );
        if (!meetingNoteIdFromEdit) {
            if (foundCompany?.analyst_name) {
                setChosenAnalyst(foundCompany.analyst_id);
            }
        }
    }, [chosenCompany, originalData]);

    useEffect(() => {
        if (!showCompanySelect && !showPotentialCompanySelect) {
            setChosenCompany(null);
        }

        if (!showPotentialCompanySelect) {
            setUseCustomCompany(false);
            setCustomCompanyName("");
        }
    }, [showCompanySelect, showPotentialCompanySelect]);

    useEffect(() => {
        if (chosenTopic) {
            const chosenTopicClassifier = topicsOptions.find(
                (t) => t.value === chosenTopic
            )?.label;

            setShowCompanySelect(
                chosenTopicClassifier === "Focus list company"
            );
            setShowPotentialCompanySelect(
                chosenTopicClassifier === "Potential focus list company"
            );
        }
    }, [chosenTopic, topicsOptions]);

    // Effect for fetching meeting details when a meeting is selected
    useEffect(() => {
        const loadMeetingDetails = async () => {
            if (!isEditing) {
                setChosenAgendaItemId("");
            }

            if (!chosenMeetingId) {
                setMeetingDetails(null);
                return;
            }

            setLoading(true);
            const details = await fetchMorningMeetingDetails(chosenMeetingId);
            if (details) {
                setMeetingDetails(details);
                if (!isEditing) {
                    setChosenDate(new Date(details.date));
                }
            }
            setLoading(false);
        };

        if (noteType === "linked") {
            loadMeetingDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenMeetingId, noteType, isEditing]);

    // Effect for setting the topic, analyst, company, and date when an agenda item is selected
    useEffect(() => {
        if (
            noteType !== "linked" ||
            isEditing ||
            !meetingDetails ||
            !chosenAgendaItemId
        )
            return;

        const agendaItem = meetingDetails.agenda_items.find(
            (item) => item.id === chosenAgendaItemId
        );
        if (!agendaItem) return;

        setChosenTopic(agendaItem.topic_classifier?.id ?? "");
        setChosenAnalyst(agendaItem.speaker ?? "");
        setChosenCompany(agendaItem.company?.id ?? null);
        setChosenDate(new Date(meetingDetails.date));
    }, [noteType, isEditing, meetingDetails, chosenAgendaItemId]);

    const isFormValid = (): boolean => {
        if (noteType === "linked" && !chosenAgendaItemId) return false;

        if (!noteText || !chosenAnalyst || !chosenTopic) return false;

        const chosenTopicClassifier = topicsOptions.find(
            (t) => t.value === chosenTopic
        )?.label;

        if (chosenTopicClassifier === "Focus list company" && !chosenCompany)
            return false;

        if (chosenTopicClassifier === "Potential focus list company") {
            if (useCustomCompany) {
                if (!customCompanyName.trim()) return false;
            } else {
                if (!chosenCompany) return false;
            }
        }

        return true;
    };

    const createCompanyIfNeeded = async (): Promise<{
        success: boolean;
        companyId: string | null;
    }> => {
        if (!useCustomCompany || noteType === "linked") {
            return { success: true, companyId: chosenCompany };
        }

        try {
            const companyResponse = await Database.post(
                "api/bqmq_voting/companies",
                { name: customCompanyName.trim() }
            );

            if (companyResponse.ok) {
                const companyData = await companyResponse.json();
                return { success: true, companyId: companyData.data.id };
            } else {
                setToast({
                    show: true,
                    type: "error",
                    message: "Failed to create company. Please try again.",
                });
                return { success: false, companyId: null };
            }
        } catch (error) {
            setToast({
                show: true,
                type: "error",
                message: "Failed to create company. Please try again.",
            });
            return { success: false, companyId: null };
        }
    };

    const handleSubmit = async () => {
        setShowLoadingToast({ show: true, message: "LOADING" });

        if (!isFormValid()) {
            setToast({
                show: true,
                type: "error",
                message: "Please ensure you have completed all fields.",
            });
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const companyResult = await createCompanyIfNeeded();
        if (!companyResult.success) {
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const parsedDate = `${chosenDate.toLocaleDateString("en-CA")}T00:00:00`;
        const noteData = {
            company_id: companyResult.companyId,
            date: parsedDate,
            analyst_id: chosenAnalyst,
            note: noteText,
            topic_classifier_id: chosenTopic,
            confirmed: meetingNoteIdFromEdit ? originalConfirmed : true,
            agenda_item_id: noteType === "linked" ? chosenAgendaItemId : null,
        };

        if (meetingNoteIdFromEdit) {
            const response: Response = await Database.patch(
                `api/bqmq_voting/meeting-note/${meetingNoteIdFromEdit}`,
                noteData
            );
            if (response.ok) {
                const data = await response.json();
                setMeetingId(data.data.id);
                setShowFileSelect(true);
                setToast({
                    show: true,
                    type: "success",
                    message: "Successfully edited the note.",
                });
            }
        } else {
            const response: Response = await Database.post(
                "api/bqmq_voting/create-new-meeting-note",
                noteData
            );
            if (response.ok) {
                const data = await response.json();
                setMeetingId(data.data.id);
                setShowFileSelect(true);
                setToast({
                    show: true,
                    type: "success",
                    message: "Successfully added a new note.",
                });
            }
        }

        setShowLoadingToast({ show: false, message: "" });
    };

    const handleFileUpload = async () => {
        setShowLoadingToast({ show: true, message: "Uploading Files" });

        if (!meetingId) {
            setToast({
                show: true,
                type: "error",
                message: "Invalid meeting note id",
            });
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        const formData = new FormData();

        if (selectedFiles.length === 0) {
            setToast({
                show: true,
                type: "error",
                message: "Please select files to upload",
            });
            setShowLoadingToast({ show: false, message: "" });
            return;
        }

        selectedFiles.forEach((file) => formData.append(file.name, file));

        try {
            const response = await Database.postFile(
                `api/bqmq_voting/meeting-note/${meetingId}/files`,
                formData
            );
            const data = await response.json();
            const uploadResponses: FileUploadResponse[] =
                data.data.uploaded_files;

            const messages = uploadResponses.map((response) => {
                switch (response.code) {
                    case 200:
                        return `Successfully uploaded ${response.file_name}`;
                    case 409:
                        return `File already exists: ${response.file_name}`;
                    default:
                        return `Failed to upload ${response.file_name}`;
                }
            });

            setToast({
                show: true,
                type: messages.every((m) => m.startsWith("Successfully"))
                    ? "success"
                    : "error",
                message: messages.join(". "),
            });
        } catch (error) {
            setToast({
                show: true,
                type: "error",
                message:
                    "An error occurred while uploading files. Please try again later.",
            });
        } finally {
            setShowLoadingToast({ show: false, message: "" });
        }
    };

    const onCompanyRadioChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const isCustom = event.target.value === "custom";
        setUseCustomCompany(isCustom);
        if (isCustom) {
            setChosenCompany(null);
        } else {
            setCustomCompanyName("");
        }
    };

    const resetForm = () => {
        setChosenCompany(null);
        setChosenAnalyst("");
        setChosenDate(new Date());
        setChosenTopic("");
        setShowCompanySelect(false);
        setShowPotentialCompanySelect(false);
        setUseCustomCompany(false);
        setCustomCompanyName("");
        setChosenMeetingId("");
        setMeetingDetails(null);
        setChosenAgendaItemId("");
        setMeetingId("");
    };

    const handleNoteTypeChange = (newType: NoteType) => {
        if (newType === noteType) return;
        resetForm();
        setNoteType(newType);
    };

    const goBack = () => {
        if (location.state?.from) {
            history.push(location.state?.from);
        } else {
            // Fallback to browser back
            history.goBack();
        }
    };

    const meetingOptions: SelectOption[] = morningMeeting.map((meeting) => ({
        value: meeting.id,
        label: `${meeting.title}`,
    }));

    const agendaItemOptions: SelectOption[] =
        meetingDetails?.agenda_items
            ?.filter((item) => !item.deleted && !item.archived)
            .map((item) => ({
                value: item.id,
                label: `${item.topic}`,
            })) ?? [];

    return (
        <NewNotesContainer fileUploadActive={!showFileSelect}>
            <div className="container">
                <div className="title-container">
                    <div className="left-container">
                        <h1 className="pageTitle">
                            {meetingNoteIdFromEdit ? "EDIT" : "NEW"} NOTE
                        </h1>
                    </div>
                    <div className="right-container">
                        {location.state?.from && (
                            <Button
                                text={location.state?.backLabel || "BACK"}
                                click={goBack}
                                icon={faArrowLeft}
                            />
                        )}
                    </div>
                </div>
                <hr />
                <div className="input-wrapper">
                    {loading ? (
                        <div className="loadingScreen">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <div className="notes-input-wrapper">
                            <div className="input-wrapper">
                                <div className="radio-group-wrapper">
                                    <label>Note type</label>
                                    <RadioGroup
                                        row
                                        value={noteType}
                                        onChange={(e) =>
                                            handleNoteTypeChange(
                                                e.target.value as NoteType
                                            )
                                        }
                                        className="note-type-radio-group"
                                    >
                                        <FormControlLabel
                                            value="standalone"
                                            control={<Radio disableRipple />}
                                            label="Standalone note"
                                            disabled={isEditing}
                                        />
                                        <FormControlLabel
                                            value="linked"
                                            control={<Radio disableRipple />}
                                            label="Linked to morning meeting"
                                            disabled={isEditing}
                                        />
                                    </RadioGroup>
                                </div>

                                {noteType === "linked" && (
                                    <div className="selection">
                                        <div
                                            className={`${
                                                isEditing ? "disabled" : ""
                                            }`}
                                        >
                                            <label>Morning meeting</label>
                                            <Select
                                                showAll={false}
                                                searchable
                                                filterName="Morning meeting"
                                                options={meetingOptions}
                                                value={chosenMeetingId}
                                                setValue={setChosenMeetingId}
                                                disabled={isEditing}
                                                fitContent={true}
                                            />
                                        </div>

                                        <div
                                            className={`${
                                                isEditing ? "disabled" : ""
                                            }`}
                                        >
                                            <label>Agenda item</label>
                                            <Select
                                                showAll={false}
                                                searchable
                                                filterName="Agenda item"
                                                options={agendaItemOptions}
                                                value={chosenAgendaItemId}
                                                setValue={setChosenAgendaItemId}
                                                disabled={
                                                    !chosenMeetingId ||
                                                    isEditing
                                                }
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="selection">
                                    <div
                                        className={
                                            isEditing || noteType === "linked"
                                                ? "disabled"
                                                : ""
                                        }
                                    >
                                        <label>Topic</label>
                                        <Select
                                            showAll={false}
                                            filterName="Topic"
                                            options={topicsOptions}
                                            value={chosenTopic}
                                            setValue={setChosenTopic}
                                            disabled={
                                                isEditing ||
                                                noteType === "linked"
                                            }
                                        />
                                    </div>

                                    {showCompanySelect && (
                                        <div
                                            className={
                                                isEditing ||
                                                noteType === "linked"
                                                    ? "disabled"
                                                    : ""
                                            }
                                        >
                                            <label>Company Select</label>
                                            <Select
                                                showAll={false}
                                                searchable
                                                filterName="Company"
                                                options={updatedCompanies}
                                                value={chosenCompany}
                                                setValue={setChosenCompany}
                                                disabled={
                                                    isEditing ||
                                                    noteType === "linked"
                                                }
                                            />
                                        </div>
                                    )}

                                    <div
                                        className={
                                            noteType === "linked"
                                                ? "disabled"
                                                : ""
                                        }
                                    >
                                        <label>Date Select</label>
                                        <DateSelect
                                            placeholder=""
                                            value={chosenDate}
                                            setValue={setChosenDate}
                                            error={false}
                                            disabled={noteType === "linked"}
                                        />
                                    </div>

                                    <div
                                        className={
                                            noteType === "linked"
                                                ? "disabled"
                                                : ""
                                        }
                                    >
                                        <label>Analyst Select</label>
                                        <Select
                                            showAll={false}
                                            searchable
                                            filterName="Analyst"
                                            options={users}
                                            value={chosenAnalyst}
                                            setValue={setChosenAnalyst}
                                            disabled={noteType === "linked"}
                                        />
                                    </div>
                                </div>
                                {showPotentialCompanySelect && (
                                    <div className="radio-group-wrapper">
                                        <label>Company Select</label>
                                        <RadioGroup
                                            value={
                                                useCustomCompany
                                                    ? "custom"
                                                    : "select"
                                            }
                                            onChange={onCompanyRadioChange}
                                            className="radio-group"
                                        >
                                            <FormControlLabel
                                                value="select"
                                                control={
                                                    <Radio disableRipple />
                                                }
                                                label="Select company"
                                                disabled={
                                                    isEditing ||
                                                    noteType === "linked"
                                                }
                                            />
                                            <FormControlLabel
                                                value="custom"
                                                control={
                                                    <Radio disableRipple />
                                                }
                                                label="Enter Custom Company"
                                                disabled={
                                                    isEditing ||
                                                    noteType === "linked"
                                                }
                                            />
                                        </RadioGroup>
                                        <div
                                            className={`radio-text-input-wrapper ${
                                                isEditing ||
                                                noteType === "linked"
                                                    ? "disabled"
                                                    : ""
                                            }`}
                                        >
                                            {useCustomCompany ? (
                                                <TextField
                                                    placeholder="Enter company name"
                                                    change={
                                                        setCustomCompanyName
                                                    }
                                                    value={customCompanyName}
                                                    disabled={
                                                        isEditing ||
                                                        noteType === "linked"
                                                    }
                                                />
                                            ) : (
                                                <Select
                                                    showAll={false}
                                                    searchable
                                                    filterName="Company"
                                                    options={
                                                        nonFocusListCompanies
                                                    }
                                                    value={chosenCompany}
                                                    setValue={setChosenCompany}
                                                    disabled={
                                                        isEditing ||
                                                        noteType === "linked"
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                <RichText
                                    setText={setNoteText}
                                    value={noteText}
                                    edit
                                />
                                <Button text="SUBMIT" click={handleSubmit} />
                            </div>

                            <div className="file-upload-wrapper">
                                <MultipleFileUpload
                                    selectedFiles={selectedFiles}
                                    setSelectedFiles={setSelectedFiles}
                                />
                                <Button
                                    text="UPLOAD FILES"
                                    click={handleFileUpload}
                                />
                                <p className="file-upload-warning">
                                    *File upload will be enabled once you submit
                                    a new note.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </NewNotesContainer>
    );
};

export default NewNotes;
